import React, { useState } from 'react';
import { COLORS, SPACING } from '../styles/theme';

type LegalTab = 'terms' | 'privacy' | 'aup';

const TABS: Array<{ id: LegalTab; label: string }> = [
  { id: 'terms', label: 'Terms' },
  { id: 'privacy', label: 'Privacy' },
  { id: 'aup', label: 'Acceptable Use' },
];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: SPACING.lg }}>
      <h3 style={{ margin: `0 0 ${SPACING.sm} 0`, fontSize: '14px', fontFamily: '"IBM Plex Mono", monospace', fontWeight: 700, fontStyle: 'italic', color: COLORS.accent }}>
        {title}
      </h3>
      <div style={{ fontSize: '13px', color: COLORS.text, lineHeight: 1.6 }}>
        {children}
      </div>
    </div>
  );
}

function TermsContent() {
  return (
    <>
      <Section title="What Authenticulture Is">
        <p>Authenticulture is a decentralized, peer-to-peer (P2P) video and social platform. There is no central
        server holding your account: your identity is a cryptographic keypair generated and stored on your own
        device. Posts, videos, and other content are synced between devices via a relay and shared directly
        between users via WebTorrent.</p>
      </Section>
      <Section title="Eligibility">
        <p>You must be at least 13 years old to use Authenticulture (16 in the EEA, or have verifiable parental
        consent). By using the service you confirm you meet this requirement.</p>
      </Section>
      <Section title="Your Content & License">
        <p>You own what you create. By posting, you grant other users — and the network of devices relaying and
        seeding that content — a license to store, cache, transmit, and re-share it as necessary for the P2P
        network to function. You're responsible for having the rights to anything you post.</p>
      </Section>
      <Section title="The P2P Persistence Trade-off — read this">
        <p>"Delete" is not absolute. Removing a post takes it out of the relay's index and official views, but
        other users who already downloaded or are seeding that content via WebTorrent may retain a copy on their
        own device — the same way deleting a torrent from one tracker doesn't delete it from everyone who
        downloaded it. We make reasonable efforts (signed takedown broadcasts) to stop further official
        distribution, but cannot guarantee removal from every device that ever received it. Don't post anything
        you aren't prepared to have persist outside your control.</p>
      </Section>
      <Section title="Moderation & Takedowns">
        <p>Anyone can report content. A designated administrator key can issue signed takedown notices, verified
        cryptographically by your client before content is removed from your view — only the designated
        admin(s) can do this.</p>
      </Section>
      <Section title="Copyright (DMCA)">
        <p>We respond to copyright notices that comply with 17 U.S.C. §512 (DMCA). If you believe content
        infringes your copyright, contact our designated agent at <strong>info@cybermidwest.com</strong> with
        the standard DMCA notice elements (identification of the work, the infringing material, your contact
        info, a good-faith statement, and a statement under penalty of perjury).</p>
      </Section>
      <Section title="Illegal Content & Mandatory Reporting">
        <p>We have a zero-tolerance policy for child sexual abuse material (CSAM). Any such content results in
        immediate, permanent removal of the associated identity and a mandatory report to NCMEC and/or law
        enforcement as required by 18 U.S.C. §2258A and applicable law — no warning, no appeal.</p>
      </Section>
      <Section title="Federation">
        <p>Other relay operators may run their own servers ("the fediverse model"). These Terms cover relays we
        operate (currently <code>relay.authenticulture.net</code>). Third-party relays have their own policies
        and we aren't responsible for them.</p>
      </Section>
      <Section title="Disclaimers & Liability">
        <p>The Service is provided "as is," without warranties of any kind. To the maximum extent permitted by
        law, we are not liable for indirect, incidental, or consequential damages, including loss of data or
        content persisting on the P2P network after removal from our relays.</p>
      </Section>
      <Section title="Termination">
        <p>We may stop accepting or relaying events from a given identity on relays we operate for violations of
        these Terms. Because identities are self-custodied, this doesn't delete your local data, keys, or content
        already replicated to other peers.</p>
      </Section>
      <Section title="Contact">
        <p>info@cybermidwest.com</p>
      </Section>
    </>
  );
}

function PrivacyContent() {
  return (
    <>
      <Section title="Overview">
        <p>There is no signup form, email, or password, and no central database of "user accounts." Your identity
        is a cryptographic keypair generated and stored on your device. We hold as little of your data as
        possible.</p>
      </Section>
      <Section title="What Stays on Your Device">
        <p>Your private key, local app settings, and locally cached media never leave your device unless you
        choose to broadcast them. Clearing your browser storage deletes all of it — including your identity —
        unless you've exported a backup via Device Transfer.</p>
      </Section>
      <Section title="What the Relay Sees">
        <p>Your public key (meant to be public), your IP address at the connection level, and any content you
        choose to publish (posts, profile, community messages, likes, event listings — all broadcast by design,
        treat as public). Direct messages are end-to-end encrypted before they reach the relay; the relay only
        ever sees ciphertext.</p>
      </Section>
      <Section title="Peer-to-Peer Sharing (WebTorrent)">
        <p>Video and media are shared directly between devices. Your IP address is visible to peers you exchange
        data with — this is inherent to how P2P/torrent technology works and isn't something we can hide.</p>
      </Section>
      <Section title="Browser Permissions">
        <p><strong>Notifications</strong> — used to alert you about replies, likes, DMs, and moderation actions
        when the app is in a background tab. Fully optional; the app works without it, and you can revoke it
        anytime in your browser settings.</p>
        <p><strong>Persistent storage</strong> — requested so your identity and seeded content aren't
        unexpectedly cleared by the browser.</p>
      </Section>
      <Section title="Third Parties">
        <p>The web client is hosted on Cloudflare Pages, which may log standard request data per its own privacy
        policy. We don't use analytics, ad trackers, or sell data to brokers — there's no algorithm company
        profiting from your attention.</p>
      </Section>
      <Section title="Children's Privacy">
        <p>Not directed at children under 13; we don't knowingly collect personal information from them
        (COPPA).</p>
      </Section>
      <Section title="Your Rights & Deletion">
        <p>You can wipe your local data anytime by clearing browser storage. You can request removal of specific
        content from our relay's index — but as explained above, this can't guarantee removal from peers who
        already received it via P2P. EEA/UK/California users: contact us about relay-side logs under
        GDPR/CCPA.</p>
      </Section>
      <Section title="Contact">
        <p>info@cybermidwest.com</p>
      </Section>
    </>
  );
}

function AUPContent() {
  return (
    <>
      <p style={{ fontSize: '13px', color: COLORS.textMuted, lineHeight: 1.6, marginBottom: SPACING.lg }}>
        This policy exists to keep Authenticulture legal to operate and usable for everyone — not to police
        opinions, taste, or politics. Consistent with the fediverse/FOSS model, we moderate illegal content and
        abusive conduct, not viewpoints.
      </p>
      <Section title="Prohibited Content">
        <ul style={{ margin: 0, paddingLeft: SPACING.lg }}>
          <li><strong>CSAM — zero tolerance.</strong> Immediate, permanent, non-appealable removal + mandatory
          report to NCMEC/law enforcement.</li>
          <li>Other content illegal under applicable law (e.g. non-consensual sexual content, illegal goods/services).</li>
          <li>Copyright-infringing material you don't have rights to share.</li>
          <li>Genuine threats, doxxing, or content enabling stalking/harassment of a specific person.</li>
          <li>Malware or files designed to compromise other users' devices.</li>
        </ul>
      </Section>
      <Section title="Prohibited Conduct">
        <ul style={{ margin: 0, paddingLeft: SPACING.lg }}>
          <li>Spam / mass-posting / flooding (circumventing the Proof-of-Work anti-spam requirement is itself a violation).</li>
          <li>Evading a ban or takedown via new identities.</li>
          <li>Attacking relay infrastructure (ours or anyone else's).</li>
          <li>Deceptive impersonation (parody/commentary is fine).</li>
          <li>Manipulating likes, tastemaker scores, or discovery via sockpuppets/bots.</li>
        </ul>
      </Section>
      <Section title="Reporting & Enforcement">
        <p>Use the in-app report feature on any post, video, or listing. Depending on severity, we may remove
        content from our relay's index, restrict a public key from publishing to our relay(s), or restrict
        community participation. Identities are self-custodied — enforcement only affects what our relay accepts
        and displays, not your local data or copies already on other peers.</p>
      </Section>
      <Section title="Appeals">
        <p>If content of yours was removed in error (other than a CSAM determination, which is final), contact
        info@cybermidwest.com with the post ID and an explanation.</p>
      </Section>
    </>
  );
}

export function Legal({ onBack }: { onBack: () => void }) {
  const [tab, setTab] = useState<LegalTab>('terms');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: SPACING.lg, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: SPACING.md, flexShrink: 0 }}>
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', color: COLORS.highlight, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', fontSize: '17px', padding: 0 }}
        >
          ← back
        </button>
        <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '17px', color: COLORS.text, fontWeight: 700 }}>
          Legal
        </span>
      </div>

      <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              flex: 1,
              padding: SPACING.md,
              background: tab === t.id ? COLORS.panel : 'transparent',
              border: 'none',
              borderBottom: `2px solid ${tab === t.id ? COLORS.accent : 'transparent'}`,
              color: tab === t.id ? COLORS.accent : COLORS.textMuted,
              cursor: 'pointer',
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '13px',
              fontWeight: tab === t.id ? 700 : 400,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.lg }}>
        <p style={{ fontSize: '11px', color: COLORS.textMuted, marginBottom: SPACING.lg, fontStyle: 'italic' }}>
          Last updated: June 2026. This is a summary — these documents are still under legal review.
        </p>
        {tab === 'terms' && <TermsContent />}
        {tab === 'privacy' && <PrivacyContent />}
        {tab === 'aup' && <AUPContent />}
      </div>
    </div>
  );
}
